// src/services/notificationService.js
import { getFunctions, httpsCallable } from "firebase/functions";

/**
 * Create a notification in Firestore (for hero dashboard)
 * @param {Object} notificationData - The notification data
 * @param {string} notificationData.type - Type of activity (e.g., "Intake Block Created", "Claim Paid", "Content Generated")
 * @param {string} notificationData.description - Description of the activity
 * @param {string} notificationData.program - Related program or entity name
 * @param {string} notificationData.intake - Related intake or identifier
 * @param {string} notificationData.status - Status (completed, pending, under_review, cancelled)
 * @param {string} notificationData.createdBy - Email of user who performed the action
 * @param {string} notificationData.createdByName - Name of user who performed the action
 * @param {Object} notificationData.metadata - Additional metadata (amount, claim details, etc.)
 * @param {string} company - Company name
 * @param {string} collectionBrand - Collection brand name
 * @returns {Promise<Object>} Result of the operation
 */
export const createNotification = async (
  notificationData,
  company,
  collectionBrand
) => {
  try {
    const functions = getFunctions();
    const firestoreOperations = httpsCallable(functions, "firestoreOperations");

    const timestamp = new Date().toISOString();
    const uniqueId = `notification_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    const notification = {
      id: uniqueId,
      type: notificationData.type,
      title: notificationData.title || "",
      description: notificationData.description || "",
      program: notificationData.program || "",
      intake: notificationData.intake || "",
      status: notificationData.status || "completed",
      createdBy: notificationData.createdBy || "",
      createdByName: notificationData.createdByName || "",
      metadata: notificationData.metadata || {},
      timestamp: timestamp,
      date: new Date(timestamp).toLocaleDateString("en-US"),
      company: company,
      collectionBrand: collectionBrand,
    };

    console.log("[NotificationService] Creating notification:", notification);

    const result = await firestoreOperations({
      operation: "write",
      company: company,
      collectionBrand: collectionBrand,
      systemName: "notifications",
      uniqueId: uniqueId,
      documentData: notification,
    });

    console.log(
      "[NotificationService] Notification created successfully:",
      result
    );
    return { success: true, id: uniqueId, data: notification };
  } catch (error) {
    console.error("[NotificationService] Error creating notification:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a learner notification in Firestore (separate from hero dashboard)
 * @param {Object} notificationData - The notification data
 * @param {string} company - Company name
 * @param {string} collectionBrand - Collection brand name
 * @returns {Promise<Object>} Result of the operation
 */
export const createLearnerNotification = async (
  notificationData,
  company,
  collectionBrand
) => {
  try {
    const functions = getFunctions();
    const firestoreOperations = httpsCallable(functions, "firestoreOperations");

    const timestamp = new Date().toISOString();
    const uniqueId = `learner_notif_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    const notification = {
      id: uniqueId,
      type: notificationData.type,
      title: notificationData.title || "",
      description: notificationData.description || "",
      program: notificationData.program || "",
      intake: notificationData.intake || "",
      status: notificationData.status || "completed",
      createdBy: notificationData.createdBy || "",
      createdByName: notificationData.createdByName || "",
      recipientEmail:
        notificationData.recipientEmail || notificationData.createdBy,
      metadata: notificationData.metadata || {},
      expiresAt:
        notificationData.expiresAt ||
        new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      timestamp: timestamp,
      date: new Date(timestamp).toLocaleDateString("en-US"),
      company: company,
      collectionBrand: collectionBrand,
    };

    console.log(
      "[NotificationService] Creating learner notification:",
      notification
    );

    const result = await firestoreOperations({
      operation: "write",
      company: company,
      collectionBrand: collectionBrand,
      systemName: "learner_notifications",
      uniqueId: uniqueId,
      documentData: notification,
    });

    console.log(
      "[NotificationService] Learner notification created successfully:",
      result
    );
    return { success: true, id: uniqueId, data: notification };
  } catch (error) {
    console.error(
      "[NotificationService] Error creating learner notification:",
      error
    );
    return { success: false, error: error.message };
  }
};

/**
 * Create a notification for intake block creation
 */
export const createIntakeBlockNotification = async (
  intakeBlockData,
  userEmail,
  userName,
  company,
  collectionBrand
) => {
  return createNotification(
    {
      type: "Intake Block Created",
      description: `New intake block "${intakeBlockData.name}" created`,
      program: intakeBlockData.programme || "Unknown Program",
      intake: intakeBlockData.startDate || "Unknown Date",
      status: "completed",
      createdBy: userEmail,
      createdByName: userName || userEmail,
      metadata: {
        intakeBlockId: intakeBlockData.uid,
        startDate: intakeBlockData.startDate,
        endDate: intakeBlockData.endDate,
        learnerCount: intakeBlockData.currentLearners || 0,
        facilitators: intakeBlockData.facilitatorIds || [],
        programType: intakeBlockData.programType || "",
        duration: intakeBlockData.duration || "",
      },
    },
    company,
    collectionBrand
  );
};

/**
 * Create a notification for claim payment
 */
export const createClaimPaymentNotification = async (
  claimData,
  paidBy,
  paidByName,
  company,
  collectionBrand
) => {
  return createNotification(
    {
      type: "Claim Paid",
      description: `Claim paid for ${claimData.supplier || "Unknown Supplier"}`,
      program: claimData.supplier || "Supplier",
      intake: claimData.invoiceNumber || "N/A",
      status: "completed",
      createdBy: paidBy,
      createdByName: paidByName || paidBy,
      metadata: {
        claimId: claimData.id,
        amount: claimData.totalAmount || "R0.00",
        supplier: claimData.supplier || "",
        invoiceNumber: claimData.invoiceNumber || "",
        claimDate: claimData.date || "",
        contactEmail: claimData.contactEmail || "",
      },
    },
    company,
    collectionBrand
  );
};

/**
 * Create a notification for content generation
 */
export const createContentGenerationNotification = async (
  contentData,
  generatedBy,
  generatedByName,
  company,
  collectionBrand
) => {
  return createNotification(
    {
      type: "Content Generated",
      description: `Content generated for "${
        contentData.programName || contentData.name
      }"`,
      program: contentData.programName || contentData.name || "Unknown Program",
      intake: contentData.duration || contentData.term || "N/A",
      status: "completed",
      createdBy: generatedBy,
      createdByName: generatedByName || generatedBy,
      metadata: {
        contentId: contentData.id || contentData.uid,
        programName: contentData.programName || contentData.name,
        duration: contentData.duration || "",
        term: contentData.term || "",
        unitsGenerated: contentData.unitsCount || 0,
        contentType: contentData.contentType || "program",
      },
    },
    company,
    collectionBrand
  );
};

/**
 * Create a notification for student registration
 */
export const createStudentRegistrationNotification = async (
  studentData,
  registeredBy,
  registeredByName,
  company,
  collectionBrand
) => {
  return createNotification(
    {
      type: "New Student Registration",
      description: `New student registered: ${
        studentData.name || studentData.email
      }`,
      program: studentData.programme || "Unknown Program",
      intake: studentData.intake || studentData.intakeBlock || "N/A",
      status: "completed",
      createdBy: registeredBy,
      createdByName: registeredByName || registeredBy,
      metadata: {
        studentEmail: studentData.email || "",
        studentName: studentData.name || "",
        programme: studentData.programme || "",
        intake: studentData.intake || "",
      },
    },
    company,
    collectionBrand
  );
};

/**
 * Create a notification for assessment completion
 */
export const createAssessmentCompletionNotification = async (
  assessmentData,
  learnerEmail,
  learnerName,
  company,
  collectionBrand
) => {
  const score = assessmentData.score || 0;
  const maxScore = assessmentData.maxScore || 100;
  const percentage =
    assessmentData.percentage || Math.round((score / maxScore) * 100);

  // Determine pass/fail status
  const passed = percentage >= 50;
  const status = passed ? "passed" : "needs-improvement";

  // Format completion time
  const completionDurationSeconds = assessmentData.completionDurationSeconds || 0;
  const minutes = Math.floor(completionDurationSeconds / 60);
  const seconds = completionDurationSeconds % 60;
  const timeTaken = minutes > 0 
    ? `${minutes}m ${seconds}s` 
    : `${seconds}s`;

  // Build assessment title with context
  const assessmentTitle = assessmentData.assessmentName || 
    assessmentData.sectionTitle || 
    "Assessment";
  
  const contextParts = [];
  if (assessmentData.moduleName) contextParts.push(assessmentData.moduleName);
  if (assessmentData.componentName) contextParts.push(assessmentData.componentName);
  if (assessmentData.unitName) contextParts.push(assessmentData.unitName);
  const context = contextParts.length > 0 ? ` (${contextParts.join(" > ")})` : "";

  return createLearnerNotification(
    {
      type: "assessment-completion",
      description: `Assessment completed: ${assessmentTitle}${context}`,
      program: assessmentData.unitName || assessmentData.courseName || "Course Assessment",
      intake: `Score: ${score}/${maxScore} (${percentage}%) • Time: ${timeTaken}`,
      status: status,
      createdBy: learnerEmail,
      createdByName: learnerName || learnerEmail,
      recipientEmail: learnerEmail,
      metadata: {
        assessmentName: assessmentTitle,
        unitName: assessmentData.unitName || "",
        outcomeName: assessmentData.outcomeName || "",
        moduleName: assessmentData.moduleName || "",
        componentName: assessmentData.componentName || "",
        score: score,
        maxScore: maxScore,
        percentage: percentage,
        passed: passed,
        completionDurationSeconds: completionDurationSeconds,
        timeTaken: timeTaken,
        completedAt: assessmentData.completedAt || new Date().toISOString(),
      },
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours expiry
    },
    company,
    collectionBrand
  );
};

export default {
  createNotification,
  createLearnerNotification,
  createIntakeBlockNotification,
  createClaimPaymentNotification,
  createContentGenerationNotification,
  createStudentRegistrationNotification,
  createAssessmentCompletionNotification,
};
